import { createMachine, assign } from 'xstate';
import * as states from './map-search-states';
import * as actions from './map-search-actions';

// outline for the context of the machine. facilitates proper
// initialization/error handling on startup
const emptyContext = {
  searchParams: undefined,
  providers: undefined,
  selectedLocation: undefined,
  fetchProviders: () => {}
};

/**
 * Returns the machine JSON for provider-list map-search. Exported to facilitate testing
 * @param {object} initialContext the initial context for the machine
 * @returns object
 */
export const machineJSON = (initialContext = emptyContext) => {
  const {
    deepLinkingParams,
    searchParams,
    providers,
    selectedLocation,
    fetchProviders,
    config
  } = initialContext;

  return {
    id: 'map-search-provider-list',
    context: {
      deepLinkingParams: deepLinkingParams,
      searchParams: searchParams,
      providers: providers,
      selectedLocation: selectedLocation,
      fetchProviders: fetchProviders,
      enableSearchThisArea: false,
      config
    },
    initial: states.INITIALIZING,

    states: {
      [states.INITIALIZING]: {
        initial: states.ROUTING,
        states: {
          [states.ROUTING]: {
            always: [
              {
                cond: 'hasProvidersInContext',
                target: states.DONE
              },
              {
                cond: 'hasSearchParamsInContext',
                target: states.FETCH_PROVIDERS
              },
              {
                target: states.ERROR
              }
            ]
          },
          [states.FETCH_PROVIDERS]: {
            invoke: {
              id: 'fetchProviders',
              src: 'fetchProviders',
              onDone: [
                {
                  cond: 'hasProviderResults',
                  actions: actions.SET_PROVIDERS,
                  target: states.DONE
                },
                {
                  actions: actions.SET_PROVIDERS,
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.DONE]: { type: 'final' },
          [states.NO_RESULTS]: { type: 'final' },
          [states.ERROR]: { type: 'final' }
        },
        onDone: [
          {
            cond: 'hasProvidersInContext',
            target: states.IDLE
          },
          {
            target: states.ERROR
          }
        ]
      },
      [states.IDLE]: {
        type: 'parallel',
        states: {
          [states.IDLE_MAP_SEARCH]: {
            always: [
              {
                cond: 'hasProvidersResultEmptyWithGeoBoundingBoxSet',
                target: `#${states.NO_RESULTS}`
              }
            ],
            on: {
              [actions.OPEN_SORT_AND_FILTER]: {
                target: `#${states.SORT_AND_FILTER_DRAWER_OPEN}`
              },
              [actions.LOCATION_PIN_CLICK]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: `#${states.PROVIDER_CARD_OPEN}`
              },
              [actions.ENABLE_SEARCH_THIS_AREA]: {
                actions: actions.ENABLE_SEARCH_THIS_AREA
              },
              [actions.SEARCH_THIS_AREA]: {
                actions: [
                  actions.UPDATE_SEARCH_PARAMS,
                  actions.DISABLE_SEARCH_THIS_AREA
                ],
                target: `#${states.FETCH_PROVIDERS}`
              }
            }
          }
        }
      },
      [states.SORT_AND_FILTER_DRAWER_OPEN]: {
        id: states.SORT_AND_FILTER_DRAWER_OPEN,
        initial: states.IDLE,
        onDone: { target: states.IDLE },
        states: {
          [states.IDLE]: {
            on: {
              [actions.CLOSE_SORT_AND_FILTER]: {
                target: states.CLOSED
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCH_PROVIDERS
              },
              [actions.CLEAR_SEARCH_FILTERS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCH_PROVIDERS
              },
              [actions.SELECT_SORT_OPTION]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCH_PROVIDERS
              }
            }
          },
          [states.FETCH_PROVIDERS]: {
            invoke: {
              id: 'fetchProviders',
              src: 'fetchProviders',
              onDone: [
                {
                  cond: 'hasProviderResults',
                  actions: actions.SET_PROVIDERS,
                  target: states.IDLE
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.NO_RESULTS]: {},
          [states.ERROR]: {},
          [states.CLOSED]: { type: 'final' }
        }
      },
      [states.PROVIDER_CARD_OPEN]: {
        id: states.PROVIDER_CARD_OPEN,
        initial: states.RENDER_PROVIDERS,
        onDone: {
          target: states.IDLE
        },
        states: {
          [states.RENDER_PROVIDERS]: {
            on: {
              [actions.CLOSE_PROVIDER_CARD]: {
                actions: actions.UNSET_SELECTED_LOCATION,
                target: states.CLOSED
              }
            }
          },
          [states.CLOSED]: {
            type: 'final'
          }
        }
      },
      [states.FETCH_PROVIDERS]: {
        id: states.FETCH_PROVIDERS,
        invoke: {
          id: 'fetchProviders',
          src: 'fetchProviders',
          onDone: [
            {
              cond: 'hasProviderResults',
              actions: actions.SET_PROVIDERS,
              target: states.IDLE
            },
            {
              actions: actions.SET_PROVIDERS,
              target: states.NO_RESULTS
            }
          ],
          onError: {
            target: states.ERROR
          }
        }
      },
      [states.NO_RESULTS]: {
        id: states.NO_RESULTS,
        on: {
          [actions.SEARCH_THIS_AREA]: {
            actions: [
              actions.UPDATE_SEARCH_PARAMS,
              actions.DISABLE_SEARCH_THIS_AREA
            ],
            target: states.FETCH_PROVIDERS
          },
          [actions.ENABLE_SEARCH_THIS_AREA]: {
            actions: actions.ENABLE_SEARCH_THIS_AREA
          }
        }
      },
      [states.ERROR]: {}
    }
  };
};

const machineActions = {
  [actions.SET_PROVIDERS]: assign({
    providers: (context, event) => event.data
  }),
  [actions.UPDATE_SEARCH_PARAMS]: assign({
    searchParams: (context, event) => {
      return {
        ...context.searchParams,
        ...event.data
      };
    }
  }),
  [actions.SET_SELECTED_LOCATION]: assign({
    selectedLocation: (context, event) => event.data
  }),
  [actions.UNSET_SELECTED_LOCATION]: assign({
    selectedLocation: () => undefined
  }),
  [actions.ENABLE_SEARCH_THIS_AREA]: assign({
    searchThisAreaEnabled: () => true
  }),
  [actions.DISABLE_SEARCH_THIS_AREA]: assign({
    searchThisAreaEnabled: () => false
  })
};

const guards = {
  hasProvidersInContext: (context) => {
    return (
      context.searchParams &&
      context.providers &&
      context.providers?._result != null
    );
  },
  hasSearchParamsInContext: (context) => context.searchParams,
  hasProviderResults: (context, event) => {
    return event.data && event.data._result && event.data._result.length > 0;
  },
  hasProvidersResultEmptyWithGeoBoundingBoxSet: (context) => {
    return (
      context.searchParams &&
      context.searchParams.geo_bounding_box &&
      context.providers?._result.length === 0
    );
  }
};

const services = {
  fetchProviders: async (context) => {
    const { fetchProviders, searchParams } = context;
    const providers = await fetchProviders(searchParams);
    return providers;
  }
};

/**
 * Creates and returns the provider-list machine that orchestrates the map-search page
 * @param {object} initialParams the initial context of the machine
 * @returns xstate.Machine
 */
export const createMapSearchProviderListMachine = (initialParams) => {
  const initialContext = { ...emptyContext, ...initialParams };
  return createMachine(machineJSON(initialContext)).withConfig({
    actions: machineActions,
    guards: guards,
    services: services
  });
};
